/**
 * Geführte Erstinventur am Tablet (R126), Route /inventur/weiter, Rollen lager und leitung, Vollbild ohne Navigation.
 *
 * Ein Artikel pro Bildschirm, großes Produktbild, große Knöpfe. Ziffernblock (inputmode numeric), Enter = speichern und
 * weiter. Jede Eingabe speichert sofort auf dem Server; nach einem Neuladen geht es beim nächsten offenen Artikel weiter.
 * Blind: kein Bestand, kein Soll (R59). Funktioniert hochkant und quer auf ca. 10-Zoll-Tablets.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { InventurArtikelKarte, InventurWeiter, InventurWeiterEingabe } from '@storeos/contracts';
import { ApiError } from '../api';
import * as inv from '../api/inventur';
import { lagerplaetze as listeLagerplaetze } from '../api/lager';
import { ton } from '../components/ton';
import { useBenutzer, useToast } from '../components/ui';

/** Produktbild direkt von der Shop-URL (CSP img-src, R126); lädt es nicht (offline, Mock-Kanal `.invalid`), stehen Initialen da. */
function Bild({ k }: { k: InventurArtikelKarte }) {
  const [kaputt, setKaputt] = useState(false);
  if (!k.bild_url || kaputt) return <span>{k.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('')}</span>;
  return <img src={k.bild_url} alt="" data-testid="iw-bild" onError={() => setKaputt(true)} />;
}

/** Manche Kanalnamen tragen die Variante schon am Ende („Klappkalender – Schwarz“); dann nicht doppelt zeigen. */
const ohneVariante = (name: string, variante: string | null) => (variante && name.endsWith(` – ${variante}`) ? name.slice(0, -(variante.length + 3)) : name);

const AKTION_TEXT = { gezaehlt: 'gezählt', ausgelaufen: 'ausgelaufen', spaeter: 'auf später gelegt' } as const;

export function InventurWeiterSeite() {
  const liste = useQuery({ queryKey: ['inventuren'], queryFn: inv.inventuren });
  const laufend = liste.data?.find((i) => i.art === 'gefuehrt_erst' && (i.status === 'offen' || i.status === 'eingereicht')) ?? null;
  if (liste.isLoading) return <div className="iw"><div className="iw-leer">Lädt …</div></div>;
  if (!laufend) {
    return (
      <div className="iw">
        <div className="iw-leer" data-testid="iw-keine">
          <h1>Keine Erstinventur offen</h1>
          <p>Die Leitung startet die geführte Erstinventur unter Inventur.</p>
          <Link className="knopf gross" to="/inventur">Zur Inventur</Link>
        </div>
      </div>
    );
  }
  return <Zaehlen inventurId={laufend.id} />;
}

function Zaehlen({ inventurId }: { inventurId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const ich = useBenutzer();
  const [artikelId, setArtikelId] = useState<string | null>(null);
  const stand = useQuery({ queryKey: ['weiter', inventurId, artikelId], queryFn: () => inv.weiter(inventurId, artikelId) });
  const plaetze = useQuery({ queryKey: ['lagerplaetze'], queryFn: listeLagerplaetze, staleTime: 60_000 });
  const [arbeitet, setArbeitet] = useState(false);
  const [spaeterOffen, setSpaeterOffen] = useState(false);

  async function sende(e: InventurWeiterEingabe, meldung: string): Promise<boolean> {
    setArbeitet(true);
    try {
      const neu = await inv.weiterErfassen(inventurId, e);
      qc.setQueryData(['weiter', inventurId, null], neu);
      setArtikelId(null);
      void qc.invalidateQueries({ queryKey: ['inventur'] });
      if (e.platz_anlegen) void qc.invalidateQueries({ queryKey: ['lagerplaetze'] });
      ton('ok'); toast(meldung);
      return true;
    } catch (err) {
      ton('fehler');
      if (err instanceof ApiError && (err.details as { code?: string })?.code === 'platz_unbekannt') throw err;
      toast((err as Error).message, 'fehler');
      return false;
    } finally { setArbeitet(false); }
  }

  const d = stand.data;
  if (stand.isLoading || !d) return <div className="iw"><div className="iw-leer">{stand.error ? (stand.error as Error).message : 'Lädt …'}</div></div>;
  const f = d.fortschritt;
  const prozent = f.gesamt ? Math.round((f.gezaehlt / f.gesamt) * 100) : 0;

  return (
    <div className="iw" data-testid="iw">
      <header className="iw-kopf">
        <Link to="/inventur" className="iw-fertig" data-testid="iw-fertig">Fertig für heute</Link>
        <div className="iw-fortschritt" aria-label="Fortschritt">
          <div className="iw-balken"><span style={{ width: `${prozent}%` }} /></div>
          <div className="iw-zahlen" data-testid="iw-fortschritt">
            <strong>{f.gezaehlt}</strong> von {f.gesamt} gezählt · heute <strong>{f.heute}</strong>
            {f.warenwert_anteil !== null && <> · Warenwert <strong>{Math.round(f.warenwert_anteil * 100)} %</strong></>}
          </div>
        </div>
        <button type="button" className="iw-chip" onClick={() => setSpaeterOffen((o) => !o)} disabled={!f.spaeter} data-testid="iw-spaeter-liste">
          Später: {f.spaeter}
        </button>
      </header>

      {spaeterOffen && d.spaeter.length > 0 && (
        <div className="iw-spaeter">
          {d.spaeter.map((s) => (
            <button key={s.article_id} type="button" className="iw-spaeter-eintrag" onClick={() => { setArtikelId(s.article_id); setSpaeterOffen(false); }}>
              <span>{s.name}{s.variante ? ` · ${s.variante}` : ''}</span><span className="mono">{s.sku}</span>
            </button>
          ))}
        </div>
      )}

      {d.inventur.status !== 'offen' ? (
        <div className="iw-leer" data-testid="iw-eingereicht"><h1>Eingereicht</h1><p>Die Leitung prüft die Zählung. Gibt sie Artikel zum Nachzählen zurück, stehen sie hier ganz vorne.</p></div>
      ) : d.aktuell ? (
        <Karte key={`${d.aktuell.article_id}-${d.aktuell.eingabe?.at ?? ''}`} k={d.aktuell} d={d} arbeitet={arbeitet} plaetze={plaetze.data?.eintraege ?? []}
          korrektur={artikelId !== null}
          onSenden={sende}
          onZurueck={() => d.letzte && setArtikelId(d.letzte.article_id)}
          onAbbrechen={() => setArtikelId(null)} />
      ) : (
        <Fertig inventurId={inventurId} d={d} rolleLeitung={ich.rolle !== 'lager'} onZurueck={() => d.letzte && setArtikelId(d.letzte.article_id)} />
      )}
    </div>
  );
}

function Karte({ k, d, arbeitet, plaetze, korrektur, onSenden, onZurueck, onAbbrechen }: {
  k: InventurArtikelKarte; d: InventurWeiter; arbeitet: boolean; plaetze: Array<{ id: string; code: string; typ: string }>; korrektur: boolean;
  onSenden: (e: InventurWeiterEingabe, meldung: string) => Promise<boolean>; onZurueck: () => void; onAbbrechen: () => void;
}) {
  const [menge, setMenge] = useState(k.eingabe && k.eingabe.aktion !== 'spaeter' && k.eingabe.gezaehlt !== null ? String(k.eingabe.gezaehlt) : '');
  const [platz, setPlatz] = useState(k.eingabe?.bin_code ?? k.bin_code ?? '');
  const [modus, setModus] = useState<'zaehlen' | 'ausgelaufen'>(k.eingabe?.aktion === 'ausgelaufen' ? 'ausgelaufen' : 'zaehlen');
  const [neuerPlatz, setNeuerPlatz] = useState<string | null>(null);
  const [platzOffen, setPlatzOffen] = useState(false);
  const feld = useRef<HTMLInputElement>(null);
  useEffect(() => { feld.current?.focus(); }, [modus]);
  const pickPlaetze = useMemo(() => plaetze.filter((p) => p.typ !== 'sonder'), [plaetze]);
  const name = `${k.name}${k.variante ? ` · ${k.variante}` : ''}`;
  const titel = ohneVariante(k.name, k.variante);

  async function speichern(e?: FormEvent, anlegen = false) {
    e?.preventDefault();
    if (arbeitet) return;
    const roh = menge.trim();
    if (modus === 'zaehlen' && roh === '') { feld.current?.focus(); return; }
    const n = roh === '' ? undefined : Number(roh);
    if (n !== undefined && (!Number.isInteger(n) || n < 0)) return;
    const code = platz.trim().toUpperCase();
    const platzGeaendert = code !== (k.bin_code ?? '').toUpperCase();
    try {
      await onSenden({
        article_id: k.article_id, aktion: modus, gezaehlt: n, ...(code && (platzGeaendert || anlegen) ? { bin_code: code } : {}), ...(anlegen ? { platz_anlegen: true } : {}),
      }, modus === 'ausgelaufen' ? `${name}: ausgelaufen${n ? `, Rest ${n}` : ''}` : `${name}: ${n} Stück`);
      setNeuerPlatz(null);
    } catch {
      setNeuerPlatz(code);   // Platz unbekannt → anlegen anbieten
    }
  }

  return (
    <main className="iw-haupt">
      <div className="iw-bild" aria-hidden>
        <Bild k={k} />
      </div>
      <form className="iw-form" onSubmit={(e) => void speichern(e)}>
        <div className="iw-text">
          <div className="iw-kategorie" data-testid="iw-kategorie">{k.kategorie ?? (k.intern ? 'Interner Artikel' : 'Ohne Kategorie')}</div>
          <h1 className="iw-name" data-testid="iw-name">{titel}</h1>
          {k.variante && <div className="iw-variante" data-testid="iw-variante">{k.variante}</div>}
          <div className="iw-sku mono" data-testid="iw-sku">{k.sku}</div>
          {k.nachzaehlen && <div className="iw-hinweis warn" data-testid="iw-nachzaehlen">Bitte nachzählen</div>}
          {korrektur && k.eingabe && <div className="iw-hinweis">Korrektur: zuletzt {AKTION_TEXT[k.eingabe.aktion]}{k.eingabe.gezaehlt !== null && k.eingabe.aktion !== 'spaeter' ? ` (${k.eingabe.gezaehlt})` : ''}</div>}
        </div>

        <label className="iw-label" htmlFor="iw-menge">{modus === 'ausgelaufen' ? 'Restmenge (leer = keine)' : 'Wie viele sind da?'}</label>
        <input id="iw-menge" ref={feld} className="iw-menge" inputMode="numeric" pattern="[0-9]*" autoComplete="off" enterKeyHint="next"
          value={menge} onChange={(e) => setMenge(e.target.value.replace(/[^0-9]/g, ''))} data-testid="iw-menge" placeholder={modus === 'ausgelaufen' ? '0' : ''} />

        <div className="iw-platz">
          {platzOffen || neuerPlatz ? (
            <>
              <label className="iw-label" htmlFor="iw-platz">Lagerplatz</label>
              <input id="iw-platz" className="iw-platz-feld mono" list="iw-plaetze" value={platz} autoCapitalize="characters" autoComplete="off"
                onChange={(e) => { setPlatz(e.target.value); setNeuerPlatz(null); }} placeholder="z. B. B-04-2" data-testid="iw-platz" />
              <datalist id="iw-plaetze">{pickPlaetze.map((p) => <option key={p.id} value={p.code} />)}</datalist>
            </>
          ) : (
            <button type="button" className="iw-platz-knopf" onClick={() => setPlatzOffen(true)} data-testid="iw-platz-aendern">
              Platz: <strong className="mono">{platz || 'keiner'}</strong> · ändern
            </button>
          )}
          {neuerPlatz && (
            <div className="iw-neu" data-testid="iw-platz-neu">
              Platz <strong className="mono">{neuerPlatz}</strong> gibt es noch nicht.
              <button type="button" className="knopf" onClick={() => void speichern(undefined, true)} disabled={arbeitet} data-testid="iw-platz-anlegen">Neu anlegen und speichern</button>
            </div>
          )}
        </div>

        <div className="iw-knoepfe">
          <button type="submit" className="iw-k iw-k-haupt" disabled={arbeitet || (modus === 'zaehlen' && menge.trim() === '')} data-testid="iw-speichern">
            {modus === 'ausgelaufen' ? 'Ausgelaufen speichern' : 'Speichern und weiter'}
          </button>
          {modus === 'zaehlen' ? (
            <button type="button" className="iw-k iw-k-aus" onClick={() => setModus('ausgelaufen')} disabled={arbeitet} data-testid="iw-ausgelaufen">Produkt ausgelaufen</button>
          ) : (
            <button type="button" className="iw-k iw-k-zweit" onClick={() => setModus('zaehlen')} disabled={arbeitet}>Doch zählen</button>
          )}
          <button type="button" className="iw-k iw-k-zweit" disabled={arbeitet}
            onClick={() => void onSenden({ article_id: k.article_id, aktion: 'spaeter' }, `${name}: später`).catch(() => undefined)} data-testid="iw-spaeter">Später</button>
          {korrektur ? (
            <button type="button" className="iw-k iw-k-zweit" onClick={onAbbrechen} disabled={arbeitet}>Abbrechen</button>
          ) : (
            <button type="button" className="iw-k iw-k-zweit" onClick={onZurueck} disabled={arbeitet || !d.letzte} data-testid="iw-zurueck"
              title={d.letzte ? `Zurück zu ${d.letzte.name}` : undefined}>Zurück</button>
          )}
        </div>
        <div className="iw-naechster" data-testid="iw-naechster">
          {d.naechster ? <>Nächster: <strong>{ohneVariante(d.naechster.name, d.naechster.variante)}{d.naechster.variante ? ` · ${d.naechster.variante}` : ''}</strong></> : 'Das ist der letzte Artikel.'}
        </div>
      </form>
    </main>
  );
}

function Fertig({ inventurId, d, rolleLeitung, onZurueck }: { inventurId: string; d: InventurWeiter; rolleLeitung: boolean; onZurueck: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  return (
    <div className="iw-leer" data-testid="iw-fertig-alles">
      <h1>Alles gezählt</h1>
      <p>{d.fortschritt.gezaehlt} Artikel. Reiche die Zählung ein, dann prüft die Leitung.{rolleLeitung ? '' : ' Bestände siehst du erst nach der Freigabe.'}</p>
      <div className="iw-knoepfe">
        <button type="button" className="iw-k iw-k-haupt" data-testid="iw-einreichen"
          onClick={async () => { try { await inv.einreichen(inventurId); toast('Eingereicht'); void qc.invalidateQueries(); } catch (e) { toast((e as Error).message, 'fehler'); } }}>
          Zählung einreichen
        </button>
        <button type="button" className="iw-k iw-k-zweit" onClick={onZurueck} disabled={!d.letzte}>Zurück</button>
      </div>
    </div>
  );
}
